import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EntityFormPage } from "@/components/entity-page/EntityFormPage";
import EditTranscriptionProviderForm from "@/components/EditTranscriptionProviderForm";
import { getFormOptions } from "@/lib/api";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/settings/transcriptionProviders/$id/")({
  component: EditTranscriptionProviderPage,
});

function EditTranscriptionProviderPage() {
  const { id } = Route.useParams();
  return <EditTranscriptionProviderPageComponent id={id} />;
}

// Exported for testing
export function EditTranscriptionProviderPageComponent({ id }: { id: string }) {
  const navigate = useNavigate();
  const [transcriptionProvider, setTranscriptionProvider] = useState<FormOption | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    let active = true;
    getFormOptions("transcriptionProviders")
      .then((options) => {
        if (!active) return;
        const found = options.find((option) => option.id === id);
        if (!found) {
          setNotFound(true);
        } else {
          setTranscriptionProvider(found);
        }
        setLoading(false);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Error fetching transcription provider:", error);
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  const goToList = () => navigate({ to: "/dashboard/settings/transcriptionProviders" });

  if (loading) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !transcriptionProvider) {
    return (
      <div className="p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Transcription provider not found</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <EntityFormPage
      title="Edit Transcription Provider"
      description={`Update ${transcriptionProvider.label}'s catalog listing.`}
      backHref="/dashboard/settings/transcriptionProviders"
      headerActions={
        !showDeleteConfirm ? (
          <Button type="button" variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        ) : undefined
      }
    >
      <EditTranscriptionProviderForm
        transcriptionProvider={transcriptionProvider}
        showDeleteConfirm={showDeleteConfirm}
        onCancelDelete={() => setShowDeleteConfirm(false)}
        onDeleted={goToList}
        onSaved={goToList}
      />
    </EntityFormPage>
  );
}
